import { useCallback } from 'react'
import Swal from 'sweetalert2'
import {
  AssinaturaType,
  AtendimentoEstado,
  ModalSize,
  showAlertLoading,
  showError,
} from '../../../../../../_metronic/helpers'
import { useModal } from '../../../../../components'
import { useCertificado } from '../../../../../helpers/hooks/useCertificado'
import { useAuth } from '../../../../auth'
import { AtendimentoEsusContexto, AtendimentoView } from '../../../atendimento/core/_models'
import {
  alteraAtendimento,
  getAtendimentoById,
  getContextoEsusEncerramento,
} from '../../../atendimento/core/_requests'
import {
  EncerramentoEsusModal,
  EncerramentoEsusValores,
} from '../../atendimento-video/modal/EncerramentoEsusModal'
import { assinarPareceresPendentes } from './useAssinarParecer'

const SEM_ESUS: AtendimentoEsusContexto = { esusAtivo: false, lotacoes: [], condutas: [] }

/** Mesma regra de `Atendimento.isDiagnosticado()` no backend, exigida para finalizar. */
export const temDiagnostico = (atendimento?: AtendimentoView) =>
  !!atendimento?.situacaoDiagnostico || !!atendimento?.cids?.length

/**
 * Como o fechamento terminou:
 * - `assinado`: interconsulta finalizada e parecer assinado (solicitante avisado);
 * - `naoAssinado`: finalizada, mas a assinatura falhou (a tela do parecer passa a oferecer
 *   "Assinar parecer", e ele também fica em Pendentes de assinatura);
 * - `semDiagnostico`: nada feito, falta diagnóstico gravado;
 * - `interrompido`: o teleconsultor desistiu (certificado ou e-SUS) ou houve erro antes de
 *   finalizar; o parecer continua salvo e nada foi encerrado.
 */
export type ResultadoFinalizacao = 'assinado' | 'naoAssinado' | 'semDiagnostico' | 'interrompido'

/**
 * Fechamento do parecer pelo teleconsultor, a partir da tela do parecer: finaliza a interconsulta e
 * assina o parecer num passo só. Não mostra alerta de saída: a tela decide o que fazer pelo retorno.
 *
 * A assinatura é obrigatória, então o certificado é pedido **antes** de finalizar: sem ele nada é
 * encerrado e o parecer continua salvo, esperando. A ordem finalizar → assinar é de propósito: o
 * backend avisa o solicitante e começa a contar os prazos de réplica e desfecho na assinatura, que
 * assim só acontece com o atendimento já encerrado. Se a assinatura falhar depois de finalizar, a
 * própria tela do parecer oferece assinar (etapa `aguardandoAssinatura`), sem reabrir.
 *
 * O backend só finaliza interconsulta com diagnóstico (CID ou situação diagnóstica); o gravado é
 * conferido de novo aqui antes do certificado, para não pedir senha e e-SUS à toa.
 */
export const useFinalizarParecer = () => {
  const { showModal } = useModal()
  const { ensurePassword } = useCertificado()
  const { currentUser } = useAuth()

  const pessoaId = currentUser?.pessoa?.id

  /** Bloco e-SUS do encerramento; `null` se o teleconsultor fechou sem confirmar. */
  const coletarDadosEsus = useCallback(
    (contexto: AtendimentoEsusContexto) =>
      new Promise<EncerramentoEsusValores | null>((resolve) => {
        showModal({
          title: 'Informações para o e-SUS',
          content: (
            <EncerramentoEsusModal
              lotacoes={contexto.lotacoes}
              condutas={contexto.condutas}
              procedimentos={contexto.procedimentos ?? []}
              onConfirm={(valores) => resolve(valores)}
            />
          ),
          size: ModalSize.Medium,
          onClose: () => resolve(null),
        })
      }),
    [showModal]
  )

  return useCallback(
    async (atendimentoId: number): Promise<ResultadoFinalizacao> => {
      try {
        if (!temDiagnostico(await getAtendimentoById(atendimentoId))) return 'semDiagnostico'
      } catch (error) {
        await showError(error)
        return 'interrompido'
      }

      let tipo: AssinaturaType
      try {
        const certificado = await ensurePassword()
        if (!certificado.ok || !certificado.tipo) return 'interrompido'
        tipo = certificado.tipo

        showAlertLoading()
        // Qualquer falha na leitura do contexto vale como integração desligada: o e-SUS nunca
        // impede o encerramento (mesma regra da tela de teleatendimento).
        const contexto = await getContextoEsusEncerramento(atendimentoId).catch(() => SEM_ESUS)
        let lotacao: number | undefined
        let condutas: number[] | undefined
        if (contexto?.esusAtivo) {
          Swal.close()
          const dados = await coletarDadosEsus(contexto)
          if (!dados) return 'interrompido'
          lotacao = dados.lotacao
          condutas = dados.condutas
          showAlertLoading()
        }

        await alteraAtendimento(
          atendimentoId,
          AtendimentoEstado.Finalizado,
          Number(pessoaId),
          false,
          null,
          lotacao,
          condutas
        )
      } catch (error) {
        await showError(error)
        return 'interrompido'
      }

      try {
        showAlertLoading()
        await assinarPareceresPendentes(atendimentoId, tipo)
      } catch (error) {
        // A interconsulta já foi encerrada: a falha fica só na assinatura, que pode ser refeita.
        await showError(error)
        return 'naoAssinado'
      }

      Swal.close()
      return 'assinado'
    },
    [ensurePassword, coletarDadosEsus, pessoaId]
  )
}
